import { Outlet, useMatches, useParams } from 'react-router'
import { ToastContainer } from 'react-toastify'

import { RouteHandle } from '@/@types/routes'
import { toastContainerSchema } from '@/@types/schemas/toast-containers'
import { BotBreadcrumbs } from '@/components/breadcrumbs'
import { Header } from '@/components/header'
import { Sidebar } from '@/components/sidebar'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useQueryParams } from '@/hooks/use-query-params'
import { MigrationFlow } from '@/pages/bot-migrations/components/migration-flow'
import { toQueryString } from '@/utils/to-query-string'

import { DashboardEditDialog } from './components/dashboards/dialogs/edit'
import { useDashboardEditDialog } from './components/dashboards/dialogs/edit/hooks/use-dashboard-edit-dialog'
import { DashboardDeleteAlertDialog } from './components/dialogs/dashboard-delete'
import { useDashboards } from './hooks/useDashboards'
import { useMenuItems } from './menu-items'

type ResolveBackLinkParams = {
  backTo: RouteHandle['backTo']
  botId?: string
  companyId?: string
  projectId?: string
}

function resolveBackLink({
  backTo,
  botId,
  companyId,
  projectId,
}: ResolveBackLinkParams): string {
  if (backTo === 'campaign-list') {
    const queryString = toQueryString({
      companyId,
      projectId,
    })

    const backLink = `/bots/${botId}/campaign/dispatches/list?${queryString}`

    return backLink
  }

  const queryString = toQueryString({
    companyId,
  })

  const backLink = `/projects/${projectId}/bots?${queryString}`

  return backLink
}

export function BotLayout() {
  const matches = useMatches()
  const currentHandle = matches.at(-1)?.handle as RouteHandle
  const { open: openDashboardEditDialog } = useDashboardEditDialog()

  const { botId } = useParams()
  const { getQueryParams } = useQueryParams()

  const { companyId, projectId } = getQueryParams()

  const backLink = resolveBackLink({
    backTo: currentHandle?.backTo,
    botId,
    companyId,
    projectId,
  })

  const dashboards = useDashboards(botId!)
  const menuItems = useMenuItems(dashboards)

  const handleDashboardsChange = () => {
    dashboards.reloadDashboards(botId!)
  }

  return (
    <>
      <ToastContainer containerId={toastContainerSchema.Enum.BOT_LAYOUT} />
      <div className="relative flex h-screen flex-col bg-studio-image bg-cover bg-center bg-no-repeat antialiased">
        <TooltipProvider>
          {openDashboardEditDialog && (
            <DashboardEditDialog onSaved={handleDashboardsChange} />
          )}
          <DashboardDeleteAlertDialog onDeleted={handleDashboardsChange} />

          <Header backLink={backLink}>
            <Header.Breadcrumbs>
              <BotBreadcrumbs />
            </Header.Breadcrumbs>

            <Header.Action>
              <MigrationFlow />
              {currentHandle?.action}
            </Header.Action>
          </Header>
          <div className="flex min-h-0 flex-1">
            <Sidebar menuItems={menuItems} theme="dark" />

            <main className="h-full w-full select-none overflow-hidden">
              <Outlet />
            </main>
          </div>
        </TooltipProvider>
      </div>
    </>
  )
}
